// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
//
// WALKING SKELETON — console-driven verification of the native token custody
// (CTO directive 2026-08-24). Runs ALONGSIDE the existing auth: nothing in the
// app consumes this yet. Exposed on `window` in dev builds only, so the whole
// flow is driven and inspected from the Safari Web Inspector:
//
//   __authVault.demo()          – login → claims → one authorized API call,
//                                 logging every byte JS receives on the way
//   __authVault.login()         – just the native login (Keychain custody)
//   __authVault.claims()        – what identity data JS can see (no token)
//   __authVault.request(url)    – native authorized GET; note it does NOT
//                                 appear in the Network tab: the request never
//                                 goes through the WebView's network stack.
//                                 Origins outside the custody allowlist (the API
//                                 bases persisted at login) are REJECTED — try
//                                 one to see the exfiltration guard fire.
//   __authVault.logout()        – wipe the Keychain entries
//
// The no-leak argument this demonstrates: every value that reaches JS is logged
// raw. There is no method that can return a token, the authorized request's
// response headers are stripped of anything auth-flavoured, and the API call is
// invisible to the WebView (no fetch/XHR happened).
import { environment } from 'src/environments/environment';
import { AuthVault, AUTH_VAULT_OAUTH } from './auth-vault';

export function exposeAuthVaultDebug(): void {
  if (environment.production) return;
  const vault = AuthVault;

  const log = (label: string, value: unknown) =>
    // eslint-disable-next-line no-console
    console.log(`[auth-vault] ${label}:`, JSON.stringify(value, null, 2));

  (window as unknown as Record<string, unknown>)['__authVault'] = {
    login: async () => {
      const res = await vault.login(AUTH_VAULT_OAUTH);
      log('login resolved with (EVERYTHING JS receives)', res);
      return res;
    },
    claims: async () => {
      const res = await vault.getClaims();
      log('getClaims resolved with', res);
      return res;
    },
    request: async (url: string) => {
      const res = await vault.authorizedRequest({ url });
      log('authorizedRequest status', res.status);
      log('authorizedRequest headers (auth-flavoured ones stripped natively)', res.headers);
      log('authorizedRequest body (first 400 chars)', res.body.slice(0, 400));
      return res;
    },
    refresh: async () => {
      const res = await vault.refresh();
      log('refresh report (no token — accessTokenChanged/refreshTokenRotated + fresh claims)', res);
      return res;
    },
    logout: async () => {
      await vault.logout();
      log('logout', 'keychain entries wiped');
    },
    // Proves silent refresh end-to-end WITHOUT waiting for real expiry, and — the
    // critical bit — that refresh-token ROTATION is persisted: a second refresh
    // can only succeed if the first correctly saved the new (one-time-use) token.
    demoRefresh: async () => {
      const before = await vault.getClaims();
      log('1. before — expiresAt', before.claims?.expiresAt);
      const r1 = await vault.refresh();
      log('2. refresh #1 — changed/rotated/new expiresAt', {
        accessTokenChanged: r1['accessTokenChanged'],
        refreshTokenRotated: r1['refreshTokenRotated'],
        expiresAt: r1['expiresAt'],
      });
      const url = `${environment.apiUrl}/api/pwi/fleets`;
      const a1 = await vault.authorizedRequest({ url });
      log('3. request after refresh #1 — status (200 = new token is valid)', a1.status);
      const r2 = await vault.refresh();
      log('4. refresh #2 — success PROVES rotation #1 was persisted', {
        accessTokenChanged: r2['accessTokenChanged'],
        refreshTokenRotated: r2['refreshTokenRotated'],
      });
      const a2 = await vault.authorizedRequest({ url });
      log('5. request after refresh #2 — status', a2.status);
      return 'demoRefresh complete — two rotations survived, both tokens stayed valid';
    },
    demo: async () => {
      log('step 1', 'native login starts — watch: no token in anything logged below');
      const { claims } = await vault.login(AUTH_VAULT_OAUTH);
      log('step 1 — login gave JS exactly this', claims);
      log('step 2', 'authorized API call through the NATIVE transport');
      const url = `${environment.apiUrl}/api/pwi/fleets`;
      const res = await vault.authorizedRequest({ url });
      log('step 2 — status', res.status);
      log('step 2 — body head', res.body.slice(0, 300));
      log('step 3', 'note the Network tab: the call above never appears in it');
      log('step 4 — storage check: localStorage keys', Object.keys(localStorage));
      return 'demo complete — everything JS ever saw is logged above';
    },
  };
  // eslint-disable-next-line no-console
  console.log(
    '[auth-vault] debug surface ready: __authVault.demo() | .demoRefresh() | .login() | .refresh() | .claims() | .request(url) | .logout()',
  );
}
